import {NoteGroupIcon} from "crqlar-react-common/dist/components/note/NoteGroupIcon";
import {useNoteGroupIndex} from "crqlar-react-common/dist/utils/hooks/notes/useNoteGroupIndex";
import {MbscEventClickEvent, Popup} from '@mobiscroll/react';
import {
    Box,
    Button,
    Chip,
    Divider,
    FormControl,
    IconButton,
    MenuItem,
    Select,
    Stack,
    Typography
} from '@mui/material';
import React, {ReactNode} from "react";
import {useMutation, useQuery} from "react-query";
import {
    getContrastingColor,
    getEventBackgroundColor,
    getEventColorBasedOnStatus
} from "crqlar-react-common/dist/utils/color.util";
import {CEventData} from "crqlar-react-common/dist/models/common/calendar/eventData.model";
import {labelizeUnderScoreText, useLocalize} from "crqlar-react-common/dist/utils/text.util";
import {
    Reservation,
    ReservationStatus
} from "crqlar-react-common/dist/models/reservation/reservation.model";
import {ReservationService} from "crqlar-react-common/dist/services/reservation/reservation.service";
import {ResourceService} from "crqlar-react-common/dist/services/resource/resource.service";
import {useAgeCategories} from "crqlar-react-common/dist/utils/hooks/ageCategories/useAgeCategories";
import {useService} from "crqlar-react-common/dist/utils/hooks/services/useServices";
import {useResources} from "crqlar-react-common/dist/utils/hooks/resources/useResources";
import {useProfile} from "crqlar-react-common/dist/utils/hooks/profile/useProfiles";
import {useCurrentSector} from "crqlar-react-common/dist/utils/hooks/sector/useSectors";
import {getCurrentStay, Profile} from 'crqlar-react-common/dist/models/profile/profile.model';
import {
    getProfileAllergiesString,
    getProfileFoodDietsString,
    getProfileFullNameReversed
} from "crqlar-react-common/dist/utils/profile.util";
import {usePhoneLayout} from "../../../utils/responsive.util";
import {formatDayMonth} from "crqlar-react-common/dist/utils/date.util";
import CachedIcon from "@mui/icons-material/Cached";
import StarIcon from "@mui/icons-material/Star";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CloseIcon from "@mui/icons-material/Close";
import HotelOutlinedIcon from "@mui/icons-material/HotelOutlined";
import NoFoodIcon from "@mui/icons-material/NoFood";
import SetMealIcon from "@mui/icons-material/SetMeal";
import {COMMON_PREFERENCE_TYPES} from "crqlar-react-common/dist/models/profile/preference.model";
import {AgeCategoriesElement} from "../../common/uiElements/AgeCategoriesElement";

interface Props {
    isTooltipOpen: boolean
    anchor?: HTMLElement
    handleTooltipClose: () => void
    event?: MbscEventClickEvent
    onMouseEnter?: () => void
    onMouseLeave?: () => void
    contentRef: React.Ref<HTMLDivElement>
    reservations?: Reservation[]
    updateReservationInCache: (updatedReservation: Reservation) => void
    onOpenReservation?: (reservation: Reservation) => void
}

interface PastVisit {
    id: string
    date: string
    parentName?: string
    childName?: string
    color?: string
}

interface InfoRowProps {
    label: string
    children: ReactNode
}

interface BodySection {
    key: string
    content: ReactNode
}

interface InlineDetailProps {
    icon: ReactNode
    children: ReactNode
}

const RESERVATION_STATUS_OPTIONS = [
    ReservationStatus.PENDING,
    ReservationStatus.CONFIRMED,
    ReservationStatus.ACTIVE,
    ReservationStatus.COMPLETED,
    ReservationStatus.NO_SHOW,
    ReservationStatus.CANCELLED
]

const formatTime = (date?: Date): string => date
    ? date.toLocaleTimeString([], {hour: '2-digit', minute: '2-digit', hour12: false})
    : ''

const InlineDetail = ({icon, children}: InlineDetailProps) => (
    <Box
        component="span"
        sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            maxWidth: '100%',
            minWidth: 0
        }}
    >
        <Box component="span" sx={{display: 'inline-flex', flexShrink: 0}}>
            {icon}
        </Box>
        <Typography
            component="span"
            sx={{
                color: 'text.secondary',
                fontSize: {xs: '0.74rem', sm: '0.8rem'},
                lineHeight: 1.3,
                overflowWrap: 'anywhere'
            }}
        >
            {children}
        </Typography>
    </Box>
)

const InfoRow = ({label, children}: InfoRowProps) => (
    <Stack
        direction="row"
        sx={{
            columnGap: {xs: 1, sm: 1.25},
            px: {xs: 1, sm: 1.5},
            py: {xs: 0.5, sm: 0.75}
        }}
    >
        <Typography
            sx={{
                width: 76,
                flexShrink: 0,
                pt: '2px',
                color: 'text.secondary',
                fontSize: {xs: '0.62rem', sm: '0.66rem'},
                fontWeight: 700,
                lineHeight: 1.25,
                letterSpacing: '0.04em',
                overflowWrap: 'anywhere'
            }}
        >
            {label.toUpperCase()}
        </Typography>
        <Box
            sx={{
                flex: 1,
                display: 'flex',
                flexWrap: 'wrap',
                gap: {xs: 0.375, sm: 0.5},
                alignItems: 'center',
                minWidth: 0
            }}
        >
            {children}
        </Box>
    </Stack>
)

const CalendarEventReservationsTooltip = (props: Props) => {
    const {t, tFallback, localize} = useLocalize()
    const noteGroupIndex = useNoteGroupIndex();
    const isMobile = usePhoneLayout()
    const popupWidth = isMobile ? 224 : 280
    const eventData = props.event?.event as CEventData | undefined
    const reservation = props.reservations?.find(r => r.id === eventData?.reservationId)

    const primaryProfile = useProfile(reservation?.primaryProfileId || reservation?.profileIds?.[0])
    const service = useService(reservation?.serviceId)
    const resources = useResources(reservation?.resourceIds || [])

    const currentStay = getCurrentStay(primaryProfile)
    const reservationStart = reservation?.expectedStartDay
    const reservationEnd = reservation?.expectedEndDay
    const allStays = [
        ...(primaryProfile?.activeStays ?? []),
        ...(primaryProfile?.pastStays ?? []),
        ...(primaryProfile?.upcomingStays ?? [])
    ]
    // Deprecated code -> now showing only the current stay and not matching stay to avoid confusion and keep the room label consistently accross the app
    // const matchingStay = allStays.find(stay =>
    //     stay.startDay &&
    //     stay.endDay &&
    //     reservationStart &&
    //     reservationEnd &&
    //     stay.startDay <= reservationStart &&
    //     stay.endDay >= reservationEnd
    // ) ?? currentStay
    const allergies = getProfileAllergiesString(primaryProfile)
    const diets = getProfileFoodDietsString(primaryProfile)
    const displayGuestCounts = reservation?.displayGuestCounts ?? reservation?.guestCounts ?? []

    const reservationAgeCategoryIds = Array.from(new Set(displayGuestCounts.flatMap(guestCount => (
        guestCount.ageCategoryId ? [guestCount.ageCategoryId] : []
    ))))
    const reservationAgeCategories = useAgeCategories(reservationAgeCategoryIds)

    const {visits: pastVisits, hiddenCount: hiddenPastVisits} = usePastVisits(primaryProfile)

    const reservationMutation = useMutation(
        async (reservationToUpdate: Reservation) => ReservationService.updateReservation(reservationToUpdate),
        {
            onSuccess: (updatedReservation: Reservation) => {
                if (updatedReservation) {
                    props.updateReservationInCache(updatedReservation)
                }
            }
        }
    )

    const headerBackgroundColor = getEventColorBasedOnStatus(reservation?.status) || '#5f6b7a'
    const headerTextColor = getContrastingColor(headerBackgroundColor) || '#ffffff'
    const statusColor = getEventColorBasedOnStatus(reservation?.status)
    const isSerial = !!reservation?.reservationGroupId
    const guestName = getProfileFullNameReversed(primaryProfile, t('Guest'))

    const parentResources = resources.filter(r => !r.parentId)
    const childResources = resources.filter(r => !!r.parentId)
    const startTime = formatTime(eventData?.start)
    const endTime = formatTime(eventData?.end)
    const timeRange = startTime && endTime ? `${startTime} – ${endTime}` : startTime || endTime
    const headerSubtitle = [
        timeRange,
        parentResources.map(r => t(r.name || '')).filter(Boolean).join(', '),
        childResources.map(r => t(r.name || '')).filter(Boolean).join(', ')
    ].filter(Boolean).join(' · ')

    const totalGuests = displayGuestCounts.reduce((sum, gc) => sum + (gc.count || 0), 0)
    const noteEntry = reservation?.notes?.find(n => n.note)
    const note = noteEntry?.note
    const room = currentStay?.roomId
    const serviceName = localize(service?.nameLocalized) || service?.name || ''
    const hasGuests = totalGuests > 0

    const bodySections: BodySection[] = []

    bodySections.push({
        key: 'stay',
        content: (
            <InfoRow label={t('Stay')}>
                {room
                    ? (
                        <InlineDetail icon={<HotelOutlinedIcon sx={{fontSize: 15, color: '#3B82F6'}}/>}>
                            {room}
                        </InlineDetail>
                    )
                    : (
                        <Typography
                            sx={{
                                fontSize: {xs: '0.74rem', sm: '0.8rem'},
                                color: 'text.secondary'
                            }}
                        >
                            -
                        </Typography>
                    )}
            </InfoRow>
        )
    })

    if (hasGuests) {
        bodySections.push({
            key: 'guests',
            content: (
                <InfoRow label={t('Guests')}>
                    <AgeCategoriesElement
                        ageCategories={reservationAgeCategories}
                        ageCategoryCounts={displayGuestCounts}
                        totalGuests={totalGuests}
                        variant="inline"
                    />
                </InfoRow>
            )
        })
    }

    if (serviceName) {
        bodySections.push({
            key: 'service',
            content: (
                <InfoRow label={t('Service')}>
                    <Typography
                        sx={{
                            overflowWrap: 'anywhere',
                            fontSize: {xs: '0.74rem', sm: '0.8rem'},
                            color: 'text.secondary'
                        }}
                    >
                        {serviceName}
                    </Typography>
                </InfoRow>
            )
        })
    }

    if (allergies.length || diets.length) {
        bodySections.push({
            key: 'preferences',
            content: (
                <InfoRow label={t('Preferences')}>
                    {Array.from(new Set(allergies)).map(allergy => (
                        <InlineDetail
                            key={`allergy-${allergy}`}
                            icon={<NoFoodIcon sx={{fontSize: 15, color: '#F25C05'}}/>}
                        >
                            {tFallback(
                                `${COMMON_PREFERENCE_TYPES.CRQLAR_FOOD_ALLERGY}_${allergy}`,
                                labelizeUnderScoreText(allergy)
                            )}
                        </InlineDetail>
                    ))}
                    {Array.from(new Set(diets)).map(diet => (
                        <InlineDetail
                            key={`diet-${diet}`}
                            icon={<SetMealIcon sx={{fontSize: 15, color: '#4CAF50'}}/>}
                        >
                            {tFallback(
                                `${COMMON_PREFERENCE_TYPES.CRQLAR_FOOD_TYPE}_${diet}`,
                                labelizeUnderScoreText(diet)
                            )}
                        </InlineDetail>
                    ))}
                </InfoRow>
            )
        })
    }

    if (note) {
        bodySections.push({
            key: 'note',
            content: (
                <InfoRow label={t('Note')}>
                    <Stack direction={'row'} alignItems={'flex-start'} gap={0.5}>
                        <NoteGroupIcon note={noteEntry} index={noteGroupIndex} fontSize={13}/>
                        <Typography
                            sx={{
                                minWidth: 0,
                                display: '-webkit-box',
                                overflow: 'hidden',
                                WebkitBoxOrient: 'vertical',
                                WebkitLineClamp: 3,
                                fontSize: {xs: '0.74rem', sm: '0.8rem'},
                                fontStyle: 'italic',
                                color: 'text.secondary'
                            }}
                        >
                            {`"${note}"`}
                        </Typography>
                    </Stack>
                </InfoRow>
            )
        })
    }

    if (pastVisits.length) {
        bodySections.push({
            key: 'past-visits',
            content: (
                <InfoRow label={t('Past visits')}>
                    <Stack sx={{rowGap: {xs: 0.25, sm: 0.5}, width: '100%'}}>
                        {pastVisits.map(visit => (
                            <Stack key={visit.id} direction="row" alignItems="center" sx={{columnGap: 0.75}}>
                                <Box
                                    sx={{
                                        width: 7,
                                        height: 7,
                                        borderRadius: '50%',
                                        flexShrink: 0,
                                        backgroundColor: visit.color || 'text.disabled'
                                    }}
                                />
                                <Typography
                                    sx={{
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        fontSize: {xs: '0.72rem', sm: '0.78rem'},
                                        color: 'text.secondary'
                                    }}
                                >
                                    {[visit.date, visit.parentName, visit.childName].filter(Boolean).join(' · ')}
                                </Typography>
                            </Stack>
                        ))}
                        {hiddenPastVisits > 0 && (
                            <Typography sx={{fontSize: {xs: '0.72rem', sm: '0.78rem'}, color: 'text.secondary'}}>
                                {`… ${t('+{{total}} more', {total: hiddenPastVisits})}`}
                            </Typography>
                        )}
                    </Stack>
                </InfoRow>
            )
        })
    }

    return (
        <Popup
            animation={false}
            contentPadding={false}
            display="anchored"
            anchor={props.anchor}
            isOpen={props.isTooltipOpen}
            scrollLock={false}
            showArrow={false}
            showOverlay={false}
            touchUi={false}
            width={popupWidth}
            onClose={props.handleTooltipClose}
        >
            <Stack
                ref={props.contentRef}
                direction="column"
                onMouseEnter={props.onMouseEnter}
                onMouseLeave={props.onMouseLeave}
            >
                <Box
                    sx={{
                        position: 'relative',
                        backgroundColor: headerBackgroundColor,
                        color: headerTextColor,
                        pl: {xs: 1, sm: 1.5},
                        pr: {xs: 1, sm: 1.5},
                        py: {xs: 0.75, sm: 1}
                    }}
                >
                    <IconButton
                        aria-label={t('Close')}
                        onClick={props.handleTooltipClose}
                        sx={{
                            position: 'absolute',
                            top: {xs: 2, sm: 3},
                            right: {xs: 2, sm: 3},
                            width: {xs: 40, sm: 36},
                            height: {xs: 40, sm: 36},
                            color: headerTextColor,
                            opacity: 0.85,
                            backgroundColor: 'transparent',
                            '&:hover': {
                                opacity: 1,
                                backgroundColor: 'rgba(255,255,255,0.08)'
                            },
                            '&:focus-visible': {
                                opacity: 1,
                                outline: `2px solid ${headerTextColor}`,
                                outlineOffset: -4
                            }
                        }}
                    >
                        <CloseIcon sx={{fontSize: 16}}/>
                    </IconButton>

                    <Stack
                        direction="row"
                        alignItems="center"
                        sx={{columnGap: 0.5, minHeight: 20, pr: {xs: 4.5, sm: 3.5}}}
                    >
                        <Typography sx={{fontSize: {xs: '0.64rem', sm: '0.69rem'}, fontWeight: 700, letterSpacing: '0.05em'}}>
                            {t(`Reservation status ${reservation?.status}`).toUpperCase()}
                        </Typography>
                        {isSerial && (
                            <Chip
                                size="small"
                                icon={<CachedIcon sx={{fontSize: 13, color: `${headerTextColor} !important`}}/>}
                                label={t('Slot type SERIAL').toUpperCase()}
                                sx={{
                                    height: {xs: 18, sm: 20},
                                    color: headerTextColor,
                                    backgroundColor: 'rgba(255,255,255,0.22)',
                                    '& .MuiChip-label': {
                                        px: 0.625,
                                        fontSize: {xs: '0.56rem', sm: '0.6rem'},
                                        fontWeight: 700,
                                        letterSpacing: '0.04em'
                                    }
                                }}
                            />
                        )}
                    </Stack>

                    <Stack
                        direction="row"
                        alignItems="center"
                        sx={{columnGap: 0.5, mt: 0.125, pr: {xs: 4.5, sm: 3.5}}}
                    >
                        <Typography
                            sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                fontSize: {xs: '0.96rem', sm: '1rem'},
                                fontWeight: 700,
                                lineHeight: 1.3
                            }}
                        >
                            {guestName}
                        </Typography>
                        {primaryProfile?.vipCode && <StarIcon sx={{fontSize: {xs: 14, sm: 16}, flexShrink: 0}}/>}
                    </Stack>

                    {headerSubtitle && (
                        <Typography
                            sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                fontSize: {xs: '0.7rem', sm: '0.76rem'},
                                opacity: 0.9,
                                mt: 0.125
                            }}
                        >
                            {headerSubtitle}
                        </Typography>
                    )}
                </Box>

                <Stack
                    divider={<Divider/>}
                    sx={{maxHeight: {xs: '45vh', sm: '55vh'}, overflowY: 'auto'}}
                >
                    {bodySections.map(section => (
                        <React.Fragment key={section.key}>{section.content}</React.Fragment>
                    ))}
                </Stack>

                <Divider/>

                <Stack
                    direction="row"
                    alignItems="stretch"
                    sx={{gap: 1, px: {xs: 1, sm: 1.5}, py: {xs: 0.625, sm: 0.875}}}
                >
                    {reservation && (
                        <FormControl
                            fullWidth
                            size="small"
                            sx={{flex: '1 1 0', minWidth: 0}}
                        >
                            <Select
                                value={reservation.status || ''}
                                disabled={reservationMutation.isLoading}
                                onChange={(event) => reservationMutation.mutate({
                                    ...reservation,
                                    status: event.target.value as ReservationStatus
                                })}
                                MenuProps={{
                                    sx: {zIndex: 100000},
                                    MenuListProps: {dense: true},
                                    PaperProps: {
                                        sx: {'& .MuiMenuItem-root': {minHeight: 40, fontSize: '0.82rem'}}
                                    }
                                }}
                                sx={{
                                    height: {xs: 36, sm: 32},
                                    backgroundColor: getEventBackgroundColor(reservation.status),
                                    color: statusColor,
                                    border: `1px solid ${statusColor}`,
                                    width: '100%',
                                    minWidth: 0,
                                    '& .MuiSelect-icon': {color: statusColor},
                                    '& .MuiOutlinedInput-notchedOutline': {border: 'none'},
                                    '& .MuiSelect-select': {
                                        py: 0.5,
                                        pl: 1,
                                        pr: '28px !important',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        fontSize: {xs: '0.72rem', sm: '0.76rem'}
                                    }
                                }}
                            >
                                {RESERVATION_STATUS_OPTIONS.map(status => (
                                    <MenuItem key={status} value={status}>
                                        {t(`Reservation status ${status}`)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}

                    {props.onOpenReservation && reservation && (
                        <Button
                            aria-label={t('Open')}
                            variant="outlined"
                            startIcon={<OpenInNewIcon sx={{fontSize: '16px !important'}}/>}
                            onClick={() => props.onOpenReservation?.(reservation)}
                            sx={{
                                flex: '1 1 0',
                                minWidth: 0,
                                height: {xs: 36, sm: 32},
                                px: {xs: 0.75, sm: 1},
                                borderColor: 'divider',
                                color: 'text.secondary',
                                fontSize: {xs: '0.72rem', sm: '0.76rem'},
                                fontWeight: 600,
                                textTransform: 'none',
                                whiteSpace: 'nowrap',
                                '& .MuiButton-startIcon': {mr: 0.5},
                                '&:hover': {
                                    borderColor: 'text.secondary',
                                    backgroundColor: 'action.hover'
                                }
                            }}
                        >
                            {t('Open')}
                        </Button>
                    )}
                </Stack>
            </Stack>
        </Popup>
    )
}

const usePastVisits = (profile?: Profile): {visits: PastVisit[], hiddenCount: number} => {
    const sector = useCurrentSector()

    const query = useQuery(
        ['tooltipPastVisits', profile?.id, sector?.id],
        async () => {
            if (!sector?.id || !profile?.id) return {visits: [] as PastVisit[], total: 0}

            const response = await ReservationService.getAllReservations({
                profileIds: [profile.id],
                sectorIds: [sector.id],
                // Past by end date, not status — dashboard-created reservations never get COMPLETED
                status: [ReservationStatus.COMPLETED, ReservationStatus.CONFIRMED, ReservationStatus.ACTIVE],
                expectedEnd: {start: new Date(0), end: new Date()},
                limit: {offset: 0, limit: 2, sort: [{field: "expectedEndDate", direction: "DESC"}]}
            })
            const reservations = response.content || []
            const resourceIds = Array.from(new Set(reservations.flatMap(r => r.resourceIds || [])))
            const resources = resourceIds.length
                ? (await ResourceService.getAllResources({
                id: resourceIds,
                limit: {offset: 0, limit: 1000}
            })).content || []
                : []

            const visits = reservations.map(r => {
                const reservationResources = resources.filter(resource => r.resourceIds?.includes(resource.id!))
                const parent = reservationResources.find(resource => !resource.parentId)
                const child = reservationResources.find(resource => !!resource.parentId)

                return {
                    id: r.id as string,
                    date: formatDayMonth(r.expectedStartDate),
                    parentName: parent?.name,
                    childName: child?.name,
                    color: parent?.meta?.tagColor
                }
            })
            return {visits, total: response.totalElements ?? visits.length}
        },
        {enabled: !!profile?.id && !!sector?.id}
    )

    const visits = query.data?.visits ?? []
    return {visits, hiddenCount: Math.max((query.data?.total ?? 0) - visits.length, 0)}
}

export default CalendarEventReservationsTooltip
